import React from 'react';
import { formatCurrency, parseFloatFromString } from '../utils/formatters';

/**
 * Single horizontal bar with three segments: Invested | Holding | Realised (proportional widths).
 * Rendered on Dashboard as its own section (below PortfolioMetrics) when there are positions.
 */
const PortfolioBreakdownBar = ({ investedAmount, holdingsValue, realisedAmount, currency }) => {
  const invested = parseFloatFromString(investedAmount || '0');
  const holding = parseFloatFromString(holdingsValue || '0');
  const realised = parseFloatFromString(realisedAmount || '0');
  const total = invested + holding + realised;

  if (total <= 0) {
    return null;
  }

  const investedPct = (invested / total) * 100;
  const holdingPct = (holding / total) * 100;
  const realisedPct = (realised / total) * 100;

  const segments = [
    { pct: investedPct, color: 'bg-violet-500 dark:bg-violet-600', title: `Invested: ${formatCurrency(investedAmount, currency)}` },
    { pct: holdingPct, color: 'bg-blue-500 dark:bg-blue-600', title: `Holding: ${formatCurrency(holdingsValue, currency)}` },
    { pct: realisedPct, color: 'bg-green-500 dark:bg-green-600', title: `Realised: ${formatCurrency(realisedAmount, currency)}` },
  ].filter((s) => s.pct > 0);

  return (
    <div>
      <p className="text-xs text-neutral-600 dark:text-neutral-400 uppercase tracking-wide mb-2">Portfolio breakdown</p>
      <div className="flex flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden min-w-0">
        {segments.map((seg, i) => (
          <div
            key={i}
            className={`h-full ${seg.color} shrink-0 ${i === 0 ? 'rounded-l' : ''} ${i === segments.length - 1 ? 'rounded-r' : ''}`}
            style={{ width: `${seg.pct}%`, minWidth: '2px' }}
            title={seg.title}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5 text-xs text-gray-600 dark:text-gray-400">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded bg-violet-500 dark:bg-violet-600" />
          Invested {formatCurrency(investedAmount, currency)}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded bg-blue-500 dark:bg-blue-600" />
          Holding {formatCurrency(holdingsValue, currency)}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded bg-green-500 dark:bg-green-600" />
          Realised {formatCurrency(realisedAmount, currency)}
        </span>
      </div>
    </div>
  );
};

export default PortfolioBreakdownBar;
